import './guild.css';

import {
  canDisband,
  canInvite,
  canKick,
  canSetMotd,
  canSetRank,
  compareMembers,
  getZone,
  GUILD_CREATE_COST,
  GUILD_MOTD_MAX,
  GUILD_NAME_MAX,
  type GuildMember,
  type GuildRank,
  type GuildState,
} from '@shefira/shared';

import { type DomAtlas, loadCharacterAtlas } from '../assets/dom-atlas.js';
import { isAction } from '../settings/keybindings.js';
import { headPortrait } from './portrait.js';
import { closeButton, hudShortcutButton } from './hud.js';
import { uiFormat, uiText } from './text.js';
import { kitTitle } from './kit.js';

export interface GuildPanelOptions {
  onCreate: (name: string, tag: string) => void;
  onInvite: (name: string) => void;
  onLeave: () => void;
  onKick: (characterId: string) => void;
  onRank: (characterId: string, rank: Exclude<GuildRank, 'leader'>) => void;
  onMotd: (text: string) => void;
  onDisband: () => void;
  onMessage: (name: string) => void;
  /** The local character's id, when known: no « Message » to oneself. */
  selfId?: () => string | undefined;
}

const RANK_LABELS: Record<GuildRank, string> = {
  leader: 'Chef',
  officer: 'Officier',
  member: 'Membre',
};

/**
 * The guild's window (ADR 0036): a HUD button with the members online, and
 * a window that is the founding form without a guild, else the motto, the
 * members by rank with where they play, and for each what my rank allows.
 * Only a view of the last `guild` the server sent; every button is an
 * intention, and the server checks the rank again.
 */
export class GuildPanel {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly body: HTMLElement;
  private readonly badge: HTMLElement;
  private readonly button: HTMLButtonElement;
  private guild: GuildState = null;
  private atlas: DomAtlas | null = null;
  /** The member whose « Exclure » was clicked once, or 'leave' / 'disband': a second click confirms. */
  private confirming?: string;
  /** The motto is being typed over. */
  private editingMotd = false;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    if (event.key === 'Escape' && !this.root.hidden) {
      this.close();
      return;
    }
    if (!isAction(event, 'guild')) return;
    event.preventDefault();
    this.toggle();
  };

  constructor(private readonly options: GuildPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'guild';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Guilde'));

    const heading = document.createElement('h2');
    heading.className = 'guild__title';
    kitTitle(heading, 'guild');
    this.title = document.createElement('span');
    heading.append(this.title);
    heading.append(
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );
    this.body = document.createElement('div');
    this.body.className = 'guild__body';
    this.root.append(heading, this.body);
    document.body.append(this.root);

    this.button = hudShortcutButton('guild', 'Guilde', 'guild');
    this.badge = document.createElement('span');
    this.badge.className = 'guild__badge';
    this.badge.hidden = true;
    this.button.append(this.badge);
    this.button.addEventListener('click', () => {
      this.toggle();
    });
    window.addEventListener('keydown', this.onKeyDown);
    this.render();
    void loadCharacterAtlas().then((atlas) => {
      this.atlas = atlas;
      this.render();
    });
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  toggle(): void {
    if (this.root.hidden) this.open();
    else this.close();
  }

  open(): void {
    this.root.hidden = false;
    this.confirming = undefined;
    this.editingMotd = false;
    this.render();
  }

  close(): void {
    this.root.hidden = true;
  }

  /** The whole guild as the server last sent it; null without one. */
  setGuild(state: GuildState): void {
    this.guild = state;
    this.confirming = undefined;
    this.editingMotd = false;
    this.render();
  }

  private render(): void {
    const guild = this.guild;
    const online = guild?.members.filter((member) => member.online).length ?? 0;
    this.badge.hidden = guild === null || online === 0;
    this.badge.textContent = String(online);
    this.body.replaceChildren();
    if (guild === null) {
      this.title.textContent = uiText('Guilde');
      this.body.append(this.creationForm());
      return;
    }
    this.title.textContent = uiText(`${guild.name} [${guild.tag}]`);
    this.body.append(this.motto(guild), this.members(guild), this.footer(guild));
  }

  /** Without a guild: a name, a tag, and the price of founding one. */
  private creationForm(): HTMLElement {
    const form = document.createElement('form');
    form.className = 'guild__create';
    const intro = document.createElement('p');
    intro.className = 'guild__hint';
    intro.textContent = uiText(
      "Tu n'es pas dans une guilde. Fonde la tienne, ou attends l'invitation d'un chef ou d'un officier.",
    );
    const name = this.input('Nom de la guilde', GUILD_NAME_MAX);
    const tag = this.input('Tag (2 à 4 lettres)', 4);
    tag.classList.add('guild__input--tag');
    const found = document.createElement('button');
    found.type = 'submit';
    found.className = 'guild__button';
    found.textContent = uiFormat('Fonder ({cost} or)', { cost: GUILD_CREATE_COST });
    const fields = document.createElement('div');
    fields.className = 'guild__fields';
    fields.append(name, tag, found);
    form.append(intro, fields);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const guildName = name.value.trim();
      const guildTag = tag.value.trim().toUpperCase();
      if (guildName.length === 0 || guildTag.length === 0) return;
      this.options.onCreate(guildName, guildTag);
    });
    return form;
  }

  /** The motto; the leader and the officers click it to type a new one. */
  private motto(guild: NonNullable<GuildState>): HTMLElement {
    const line = document.createElement('div');
    line.className = 'guild__motd';
    if (this.editingMotd) {
      const input = this.input('Devise de la guilde', GUILD_MOTD_MAX);
      input.value = guild.motd;
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.editingMotd = false;
          this.options.onMotd(input.value.trim());
          this.render();
        } else if (event.key === 'Escape') {
          this.editingMotd = false;
          this.render();
        }
      });
      line.append(input);
      window.requestAnimationFrame(() => {
        input.focus();
        input.select();
      });
      return line;
    }
    const text = document.createElement('span');
    text.className = 'guild__motd-text';
    text.textContent =
      guild.motd.length > 0
        ? uiFormat('« {motto} »', { motto: guild.motd })
        : uiText('Aucune devise.');
    if (guild.motd.length === 0) text.classList.add('guild__motd-text--empty');
    line.append(text);
    if (canSetMotd(guild.myRank)) {
      const edit = document.createElement('button');
      edit.type = 'button';
      edit.className = 'guild__button guild__button--small';
      edit.textContent = uiText('Modifier');
      edit.title = uiText('Changer la devise');
      edit.addEventListener('click', () => {
        this.editingMotd = true;
        this.render();
      });
      text.addEventListener('click', () => {
        this.editingMotd = true;
        this.render();
      });
      text.classList.add('guild__motd-text--editable');
      line.append(edit);
    }
    return line;
  }

  private members(guild: NonNullable<GuildState>): HTMLElement {
    const list = document.createElement('ul');
    list.className = 'guild__list';
    const online = guild.members.filter((member) => member.online).length;
    const count = document.createElement('li');
    count.className = 'guild__count';
    count.textContent = uiFormat(
      guild.members.length > 1
        ? '{count} membres, {online} en ligne'
        : '{count} membre, {online} en ligne',
      { count: guild.members.length, online },
    );
    list.append(count);
    for (const member of [...guild.members].sort(compareMembers)) {
      list.append(this.row(guild, member));
    }
    return list;
  }

  private row(guild: NonNullable<GuildState>, member: GuildMember): HTMLLIElement {
    const row = document.createElement('li');
    row.className = 'guild__row';
    row.dataset.online = String(member.online);
    row.dataset.away = String(member.online && member.away);
    row.dataset.rank = member.rank;
    const self = this.options.selfId?.() === member.id;

    const who = document.createElement('div');
    who.className = 'guild__who';
    const name = document.createElement('span');
    name.className = 'guild__name';
    name.textContent = uiText(member.name);
    const rank = document.createElement('span');
    rank.className = 'guild__rank';
    rank.textContent = uiText(RANK_LABELS[member.rank]);
    const where = document.createElement('span');
    where.className = 'guild__where';
    where.textContent = whereabouts(member);
    who.append(name, rank, where);

    const level = document.createElement('span');
    level.className = 'guild__level';
    level.textContent = uiFormat('Niv. {level}', { level: member.level });

    const actions = document.createElement('div');
    actions.className = 'guild__actions';
    const action = (label: string, run: () => void): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'guild__button';
      button.textContent = uiText(label);
      button.addEventListener('click', run);
      actions.append(button);
      return button;
    };
    if (member.online && !self) {
      action('Message', () => {
        this.close();
        this.options.onMessage(member.name);
      });
    }
    if (!self && member.rank === 'member' && canSetRank(guild.myRank, 'officer')) {
      action('Promouvoir', () => {
        this.options.onRank(member.id, 'officer');
      });
    }
    if (!self && member.rank === 'officer' && canSetRank(guild.myRank, 'member')) {
      action('Rétrograder', () => {
        this.options.onRank(member.id, 'member');
      });
    }
    if (!self && canKick(guild.myRank, member.rank)) {
      const confirming = this.confirming === member.id;
      const kick = action(confirming ? 'Sûr ?' : 'Exclure', () => {
        if (this.confirming === member.id) {
          this.confirming = undefined;
          this.options.onKick(member.id);
        } else {
          this.confirming = member.id;
          this.render();
        }
      });
      kick.classList.add('guild__button--remove');
    }
    // Away, a member is an empty slot: the server only knows how players in the game look.
    const slot = document.createElement('div');
    slot.className = 'guild__portrait';
    if (member.portrait !== null && this.atlas !== null) {
      slot.append(headPortrait(this.atlas, member.portrait));
    }
    row.append(slot, who, level, actions);
    return row;
  }

  /** Invite by name when my rank allows, then leave, and for the leader disband. */
  private footer(guild: NonNullable<GuildState>): HTMLElement {
    const footer = document.createElement('div');
    footer.className = 'guild__footer';
    if (canInvite(guild.myRank)) {
      const form = document.createElement('form');
      form.className = 'guild__invite';
      const input = this.input('Inviter un joueur', 16);
      const invite = document.createElement('button');
      invite.type = 'submit';
      invite.className = 'guild__button';
      invite.textContent = uiText('Inviter');
      form.append(input, invite);
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const name = input.value.trim();
        if (name.length === 0) return;
        input.value = '';
        this.options.onInvite(name);
      });
      footer.append(form);
    }
    const buttons = document.createElement('div');
    buttons.className = 'guild__footer-actions';
    const confirmable = (
      key: 'leave' | 'disband',
      label: string,
      run: () => void,
    ): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'guild__button guild__button--remove';
      button.textContent = uiText(this.confirming === key ? 'Sûr ?' : label);
      button.addEventListener('click', () => {
        if (this.confirming === key) {
          this.confirming = undefined;
          run();
        } else {
          this.confirming = key;
          this.render();
        }
      });
      buttons.append(button);
      return button;
    };
    confirmable('leave', 'Quitter', () => {
      this.options.onLeave();
    });
    if (canDisband(guild.myRank)) {
      confirmable('disband', 'Dissoudre', () => {
        this.options.onDisband();
      });
    }
    footer.append(buttons);
    return footer;
  }

  /** A text field of the window: typing in it never reaches the game. */
  private input(placeholder: string, maxLength: number): HTMLInputElement {
    const input = document.createElement('input');
    input.className = 'guild__input';
    input.maxLength = maxLength;
    input.autocomplete = 'off';
    input.placeholder = uiText(placeholder);
    input.setAttribute('aria-label', uiText(placeholder));
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') input.blur();
      event.stopPropagation();
    });
    return input;
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.root.remove();
    this.button.remove();
  }
}

/** Where they play, or « Absent · Havrebourg » while away from the keyboard (ADR 0080). */
function whereabouts(who: { online: boolean; away: boolean; zoneId: string | null }): string {
  if (!who.online) return uiText('Hors ligne');
  const place = uiText(getZone(who.zoneId ?? '')?.name ?? 'En ligne');
  return who.away ? uiFormat('Absent · {place}', { place }) : place;
}
